import React, { useEffect, useState } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PLATFORMS, CONTENT_TYPES, CONTENT_STATUS } from '@/lib/constants';
import { uploadFile, fileNameFromUrl } from '@/lib/storage';

export default function ContentSlideOver({ open, onClose, item, onSaved, projects = [], campaigns = [], defaultDate }) {
    const { profile } = useAuth();
    const isEdit = !!item?.id;
    const [form, setForm] = useState({});
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(item ? { ...item } : { title: '', platform: 'Instagram', content_type: 'Post', status: 'Idea', planned_date: defaultDate || '' });
        setFile(null);
    }, [open, item?.id, defaultDate]);

    if (!open) return null;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null;

            if (file) {
                const { publicUrl } = await uploadFile({ folder: 'content', file, organizationId: profile.organization_id });
                payload.asset_url = publicUrl;
            }

            if (isEdit) {
                const { error } = await supabase.from('content_calendar').update(payload).eq('id', item.id);
                if (error) throw error;
            } else {
                payload.organization_id = profile.organization_id;
                payload.created_by = profile.id;
                const { error } = await supabase.from('content_calendar').insert(payload);
                if (error) throw error;
            }
            toast.success(isEdit ? 'Content updated' : 'Content scheduled');
            onSaved?.();
            onClose();
        } catch (err) { toast.error(err.message); } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/40" />
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" data-testid="content-slideover">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold">{isEdit ? 'Edit content' : 'Schedule content'}</h2>
                    <button type="button" onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Title *"><input required value={form.title || ''} onChange={set('title')} className="input" data-testid="content-title" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Platform *"><select required value={form.platform || 'Instagram'} onChange={set('platform')} className="input" data-testid="content-platform">{PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
                        <Field label="Content type"><select value={form.content_type || ''} onChange={set('content_type')} className="input"><option value="">—</option>{CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
                        <Field label="Planned date"><input type="date" value={form.planned_date || ''} onChange={set('planned_date')} className="input" /></Field>
                        <Field label="Status"><select value={form.status || 'Idea'} onChange={set('status')} className="input">{CONTENT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                        <Field label="Linked project"><select value={form.linked_project_id || ''} onChange={set('linked_project_id')} className="input"><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}</select></Field>
                        <Field label="Linked campaign"><select value={form.linked_campaign_id || ''} onChange={set('linked_campaign_id')} className="input"><option value="">—</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
                    </div>
                    <Field label="Caption draft"><textarea rows={3} value={form.caption_draft || ''} onChange={set('caption_draft')} className="input" /></Field>
                    <Field label="YouTube/External URL"><input value={form.youtube_url || ''} onChange={set('youtube_url')} className="input" placeholder="https://..." /></Field>
                    <Field label="Asset (image/video)">
                        <div className="flex items-center gap-2">
                            <label className="flex-1 h-10 px-3 rounded-md border border-dashed border-gray-300 hover:border-aks-gold inline-flex items-center gap-2 cursor-pointer text-[13px] text-gray-600">
                                <Upload className="h-4 w-4" />
                                <span className="truncate">{file?.name || (form.asset_url ? fileNameFromUrl(form.asset_url) : 'Upload asset')}</span>
                                <input type="file" className="hidden" accept="image/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            </label>
                            {form.asset_url && <a href={form.asset_url} target="_blank" rel="noreferrer" className="h-10 px-3 inline-flex items-center gap-1.5 rounded-md border border-gray-300 text-[12px] font-semibold text-aks-navy"><FileText className="h-4 w-4" /> View</a>}
                        </div>
                    </Field>
                    <Field label="Notes"><textarea rows={2} value={form.notes || ''} onChange={set('notes')} className="input" /></Field>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
                    <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md border border-gray-300 font-medium">Cancel</button>
                    <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-aks-navy text-white font-semibold disabled:opacity-60" data-testid="content-submit">{saving ? 'Saving…' : 'Save content'}</button>
                </div>
                <style>{`.input{margin-top:.25rem;width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #d1d5db;border-radius:.375rem;outline:none;font-size:14px;background:#fff}.input:focus{border-color:#C9A84C;box-shadow:0 0 0 3px rgba(201,168,76,.25)}textarea.input{height:auto;padding:.5rem .75rem}`}</style>
            </form>
        </div>
    );
}

function Field({ label, children }) { return <label className="block"><span className="text-[12px] font-medium text-aks-text">{label}</span>{children}</label>; }
